/**
 * Save the flow as a draft, or save and start it. With an agent as the runner,
 * a new experiment is created with its target (the agent row is upserted on the
 * server); a saved draft is patched, including its connection and version.
 * With the dataset's outputs as the runner it starts at once (no draft): a
 * dataset's outputs are scored as they are (`/experiments/import`; a live
 * dataset's chosen version by number), and fresh traces of a live dataset are
 * pulled first (`/experiments/pull`). An agent on fresh traces sends the period
 * with the experiment and pulls when it starts.
 */

import { useRef, useState } from "react";
import { useNavigate } from "react-router";
import { errorText } from "@/components/ui/states";
import { useUiStore } from "@/stores/ui-store";
import { useCreateExperiment, useImportRecorded, usePullExperiment, useUpdateDraft } from "../hooks";
import { experimentFields, liveFields } from "./new-experiment-payload";
import type { EvaluatorChoice, ExperimentSummary } from "../types";
import type { NewExperiment } from "./use-new-experiment";

export function useSubmitExperiment(
  flow: NewExperiment,
  draft: ExperimentSummary | undefined,
  onSaved: () => void = () => {},
) {
  const create = useCreateExperiment();
  const update = useUpdateDraft(draft?.id ?? "");
  const scoreOutputs = useImportRecorded();
  const pull = usePullExperiment();
  const notify = useUiStore((s) => s.notify);
  const navigate = useNavigate();
  // Set before the first await, so a second click (a double-click) sends nothing.
  const sending = useRef(false);
  const [held, setHeld] = useState(false);
  const hold = (on: boolean) => {
    sending.current = on;
    setHeld(on);
  };

  const submit = async (start: boolean) => {
    if (!flow.datasetId || sending.current) return;
    hold(true);
    try {
      if (!(await send(flow.datasetId, start))) hold(false);
    } catch {
      hold(false);
    }
  };

  /** False when nothing was sent, so the button frees up again. */
  const send = async (datasetId: string, start: boolean): Promise<boolean> => {
    const failed = (e: unknown) => {
      hold(false);
      notify(errorText(e, "Could not save the experiment."), "error");
    };
    const chosen = await flow.fixes.payload(flow.evaluatorIds).catch((e: unknown) => {
      notify(errorText(e, "Could not save the evaluator's new default."), "error");
      return null;
    });
    if (!chosen) return false;
    const done = (e: ExperimentSummary) => {
      onSaved();
      if (!start) notify("Draft saved.");
      navigate(`/experiments/${e.id}`);
    };
    if (flow.runner === "outputs") {
      const evaluators: EvaluatorChoice[] =
        "evaluators" in chosen ? chosen.evaluators : chosen.evaluator_ids.map((id) => ({ evaluator_id: id }));
      const named = {
        name: flow.form.name.trim(),
        description: flow.form.description.trim(),
        dataset_id: datasetId,
        evaluators,
      };
      const live = flow.live ? liveFields(flow) : {};
      if (live.period)
        pull.mutate({ ...named, ...live, period: live.period }, { onSuccess: done, onError: failed });
      else if (!flow.live || live.dataset_version)
        scoreOutputs.mutate({ ...named, from_dataset: true, ...live }, { onSuccess: done, onError: failed });
      else return false;
      return true;
    }
    const target = flow.target.payload();
    if (!target) return false;
    const fields = experimentFields(flow, datasetId, chosen);
    if (draft) {
      const patch = { ...fields, version: target.version };
      update.mutate({ patch, start }, { onSuccess: done, onError: failed });
    } else create.mutate({ ...fields, ...target, start }, { onSuccess: done, onError: failed });
    return true;
  };

  const busy = create.isPending || update.isPending || scoreOutputs.isPending || pull.isPending;
  return { submit, busy: busy || held || flow.fixes.busy };
}
